import { isNumber } from 'radash';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import { ConnectionHelpers } from '@diagram/helpers/ConnectionHelpers';
import { PowerFlowHelpers } from '@diagram/helpers/PowerFlowHelpers';
import { ComponentInstanceService } from '@diagram/services/ComponentInstanceService';
import { diagram } from '@diagram/state/diagram';

import type { Diagram, DiagramComponentInstance, DiagramConnection, PowerFlowDirection } from 'models';
import { SyncEngineHelpers } from 'helpers/SyncEngineHelpers';

import { LocalNotificationService } from 'services/LocalNotificationService';

export type RelativePowerFlowDirection = 'load' | 'bidirectional' | 'generate';

export type PortReference = {
    componentInstanceId: DiagramComponentInstance['id'];
    port: number;
};

export type PortPowerFlowChange = PortReference & {
    powerFlowDirection: PowerFlowDirection;
};

export type PowerFlowPlan =
    | { possible: true; changes: PortPowerFlowChange[] }
    | { possible: false; blockedBy: PortReference | null; reason: string };

const PowerFlowService = {
    /**
     * Wires a port with a direction, together with every port the change reaches (see getPowerFlowPlan). When that
     * isn't possible it shows why and changes nothing. Returns whether the change was made; callers save.
     */
    setPowerFlowConfiguration: (
        componentInstance: DiagramComponentInstance,
        portIndex: DiagramConnection['from' | 'to']['port'],
        powerFlowDirection: PowerFlowDirection,
    ): boolean => {
        if (portIndex === null) {
            return false;
        }

        const plan = PowerFlowService.getPowerFlowPlan(
            { componentInstanceId: componentInstance.id, port: portIndex },
            powerFlowDirection,
        );

        return PowerFlowService.applyPowerFlowPlan(plan);
    },

    /**
     * Declares which way a protection device can carry power: on port 0, with port 1 reversed. The declaration is
     * what the device can carry, so a one-way declaration first needs the wires through it to flow one way. It keeps
     * the way power already flows, or runs from port 0 to port 1, and changes nothing when the wiring can't follow.
     */
    setPowerFlowSpecification: (
        componentInstance: DiagramComponentInstance,
        powerFlowDirection: PowerFlowDirection,
    ) => {
        const instance = diagram.componentInstances[componentInstance.id];
        const wired = instance.configuration.ports[0]?.powerFlowDirection;
        const declared =
            powerFlowDirection === 'bidirectional' || !wired || wired === 'bidirectional' ? powerFlowDirection : wired;

        if (declared !== 'bidirectional' && !PowerFlowService.setPowerFlowConfiguration(instance, 0, declared)) {
            return;
        }

        const reversed = PowerFlowHelpers.getReversedPowerFlowDirection(declared);

        SyncEngineHelpers.set(instance, 'specifications.electrical.ports.0.powerFlowDirection', declared);
        SyncEngineHelpers.set(instance, 'specifications.electrical.ports.1.powerFlowDirection', reversed);
    },

    /**
     * Works out every port that has to change for `start` to get `powerFlowDirection`, without changing anything.
     * Each connection joins two ports with opposite directions, and power that enters a pass-through
     * component (see PowerFlowHelpers.getPassThroughPort) leaves through its other port. The walk stops where nothing
     * needs to change and the wire already has a direction, and fails on the first port that can't take the
     * direction it would need. Components that only might pass power through are walked through as well, unless
     * `guessPassThrough` is off; when that blocks the plan, it is worked out again without them. `wiring` plans for
     * connections that aren't made yet, so a change that can't happen never has to be written and undone.
     */
    getPowerFlowPlan: (
        start: PortReference,
        powerFlowDirection: PowerFlowDirection,
        { guessPassThrough = true, wiring = diagram }: { guessPassThrough?: boolean; wiring?: Diagram } = {},
    ): PowerFlowPlan => {
        const visited = new Set<string>();
        const changes: PortPowerFlowChange[] = [];

        const steps: PortPowerFlowChange[] = [{ ...start, powerFlowDirection }];

        for (const step of steps) {
            const componentInstance = wiring.componentInstances[step.componentInstanceId];
            const key = `${step.componentInstanceId}:${step.port}`;

            // Every port has one wire and at most one pass-through partner, and both reverse the direction, so loops
            // have an even length and a port reached twice always needs the same direction.
            if (!componentInstance || visited.has(key)) {
                continue;
            }

            visited.add(key);

            if (
                !PowerFlowHelpers.getAllowedPowerFlowDirections(componentInstance, step.port).includes(
                    step.powerFlowDirection,
                )
            ) {
                // A bus or panel with two connections might feed both instead of passing power on, like a panel
                // feeding two loads, so that guess is dropped when it can't work.
                if (guessPassThrough) {
                    return PowerFlowService.getPowerFlowPlan(start, powerFlowDirection, {
                        guessPassThrough: false,
                        wiring,
                    });
                }

                const designator =
                    componentInstance.designator ||
                    ComponentInstanceHelpers.generateDesignator(
                        componentInstance.componentType,
                        componentInstance.indicator,
                    );

                return {
                    possible: false,
                    blockedBy: { componentInstanceId: step.componentInstanceId, port: step.port },
                    reason: {
                        input: `${designator} can't take power in on that side`,
                        output: `${designator} can't send power out on that side`,
                        bidirectional: `${designator} can't carry power both ways`,
                    }[step.powerFlowDirection],
                };
            }

            const changed =
                componentInstance.configuration.ports[step.port]?.powerFlowDirection !== step.powerFlowDirection;

            if (changed) {
                changes.push(step);
            }

            const walksOn = changed || step === steps[0];
            const reversed = PowerFlowHelpers.getReversedPowerFlowDirection(step.powerFlowDirection);
            const otherEndpoint = ConnectionHelpers.getOtherEndpoint(wiring, step.componentInstanceId, step.port);

            if (otherEndpoint && isNumber(otherEndpoint.port)) {
                const otherPowerFlowDirection =
                    wiring.componentInstances[otherEndpoint.componentInstanceId]?.configuration.ports[
                        otherEndpoint.port
                    ]?.powerFlowDirection;

                // A port that keeps its direction can still sit on a wire without one, like after a flip or in old
                // diagrams: both ends take power in, both send it out, or the far end has no direction yet.
                const wireLacksDirection =
                    !otherPowerFlowDirection ||
                    (otherPowerFlowDirection === step.powerFlowDirection &&
                        otherPowerFlowDirection !== 'bidirectional');

                if (walksOn || wireLacksDirection) {
                    steps.push({
                        componentInstanceId: otherEndpoint.componentInstanceId,
                        port: otherEndpoint.port,
                        powerFlowDirection: reversed,
                    });
                }
            }

            const passThroughPort = PowerFlowHelpers.getPassThroughPort(
                componentInstance,
                step.port,
                ComponentInstanceService.getConnectedPorts(componentInstance, wiring),
                guessPassThrough,
            );

            if (walksOn && passThroughPort !== null) {
                steps.push({
                    componentInstanceId: step.componentInstanceId,
                    port: passThroughPort,
                    powerFlowDirection: reversed,
                });
            }
        }

        return { possible: true, changes };
    },

    /**
     * Of the plans for giving `start` each of the `candidates` directions, the possible one that changes the fewest
     * ports, or null when none is possible.
     *
     * For when a direction is only a suggestion, like on a new connection or a freshly linked product. Taking the
     * candidate that changes the fewest ports continues the flow already running through a component rather than
     * reversing it; earlier candidates win a tie. It doesn't guess that a bus or panel passes power through: that
     * only ever changes more ports, like reversing the grid wire on a bus when solar is added to it.
     */
    getBestPowerFlowPlan: (start: PortReference, candidates: PowerFlowDirection[]): PowerFlowPlan | null => {
        const [plan] = candidates
            .map((powerFlowDirection) =>
                PowerFlowService.getPowerFlowPlan(start, powerFlowDirection, { guessPassThrough: false }),
            )
            .flatMap((plan) => (plan.possible ? [plan] : []))
            .sort((first, second) => first.changes.length - second.changes.length);

        return plan ?? null;
    },

    /**
     * Writes a plan's changes to the diagram, or shows why it isn't possible. Returns whether it was applied; callers
     * save.
     */
    applyPowerFlowPlan: (plan: PowerFlowPlan): boolean => {
        if (!plan.possible) {
            LocalNotificationService.showInfo({ message: plan.reason });

            return false;
        }

        plan.changes.forEach(({ componentInstanceId, port, powerFlowDirection }) => {
            const portConfiguration = diagram.componentInstances[componentInstanceId]?.configuration.ports[port];

            if (portConfiguration) {
                portConfiguration.powerFlowDirection = powerFlowDirection;
            }
        });

        return true;
    },
};

export { PowerFlowService };
